import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "admin", "editor", "order_manager", "viewer"];

export default function AdminUsers() {
  const { admin: me, hasPerm } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const canManage = hasPerm("manage_users");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!canManage) {
    return <AdminLayout title="Users"><div className="text-zinc-500" data-testid="users-forbidden">You do not have permission to manage users.</div></AdminLayout>;
  }

  const updateRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleDisabled = async (id, disabled) => {
    try { await api.put(`/admin/users/${id}`, { disabled: !disabled }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id, email) => {
    if (!window.confirm(`Delete ${email}?`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const forceReset = async (id) => {
    try { await api.put(`/admin/users/${id}`, { must_reset_password: true }); toast.success("Force-reset flag set"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout title="Admin users" actions={
      <button data-testid="users-new-btn" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 h-10 text-sm rounded-sm">
        <Plus size={14} /> New user
      </button>
    }>
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-5 py-3">Email / Username</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Force Reset</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center text-zinc-500 py-10">Loading…</td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.email}`}>
                <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-5 py-3">{u.name || "—"}</td>
                <td className="px-5 py-3">
                  <select
                    value={u.role}
                    onChange={(e) => updateRole(u.id, e.target.value)}
                    disabled={u.id === me?.id}
                    className="text-xs border border-zinc-200 px-2 py-1 rounded bg-white"
                    data-testid={`user-role-${u.email}`}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3">
                  <button onClick={() => toggleDisabled(u.id, u.disabled)} disabled={u.id === me?.id} data-testid={`user-toggle-${u.email}`} className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border rounded ${u.disabled ? "bg-zinc-100 text-zinc-500 border-zinc-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                    {u.disabled ? "disabled" : "active"}
                  </button>
                </td>
                <td className="px-5 py-3">
                  {u.must_reset_password ? (
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded">required</span>
                  ) : (
                    <button onClick={() => forceReset(u.id)} className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
                      <Lock size={10} /> Force
                    </button>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => remove(u.id, u.email)} disabled={u.id === me?.id} data-testid={`user-delete-${u.email}`} className="text-zinc-400 hover:text-red-600 disabled:opacity-30"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </AdminLayout>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer", must_reset_password: true });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      onCreated();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white border border-zinc-200 rounded-md p-8 w-full max-w-md shadow-2xl" data-testid="create-user-modal">
        <h2 className="font-display text-xl mb-5">New admin user</h2>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="cu-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email or username" className="w-full h-10 px-3 border border-zinc-300 text-sm" />
          <input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Display name (optional)" className="w-full h-10 px-3 border border-zinc-300 text-sm" />
          <input data-testid="cu-pw" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password (≥ 6 chars)" className="w-full h-10 px-3 border border-zinc-300 text-sm" />
          <select data-testid="cu-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 px-3 border border-zinc-300 text-sm bg-white">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.must_reset_password} onChange={(e) => setForm({ ...form, must_reset_password: e.target.checked })} />
            Force password reset on first login
          </label>
          <div className="flex items-center justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="text-sm text-zinc-500 px-3 h-10">Cancel</button>
            <button data-testid="cu-submit" disabled={loading} className="bg-zinc-900 text-white text-sm px-5 h-10 rounded-sm">{loading ? "Creating…" : "Create"}</button>
          </div>
        </form>
      </div>
    </>
  );
}
